/**
 * useSnapDetection - Hook para detectar snap a nodos y edges cercanos
 *
 * @version 1.0
 * @created 2026-03-06
 * @module core/spatial/hooks
 *
 * @description
 * Durante la creación de ramas, detecta nodos y edges cercanos al cursor
 * para permitir snap automático. Calcula el punto más cercano en edges.
 *
 * @example
 * ```tsx
 * const { snapTarget, snapDistance } = useSnapDetection({
 *   enabled: editMode === 'create-branch',
 *   cursorPosition: [lng, lat],
 *   excludeNodeId: sourceNodeId,
 *   snapThreshold: 20, // píxeles
 * });
 * ```
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type maplibregl from 'maplibre-gl';
import { mapEngine } from '../../map-engine/MapEngineService';

/**
 * Calcula el punto más cercano en un segmento de línea
 */
function getClosestPointOnSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): { x: number; y: number } {
  const dx = x2 - x1;
  const dy = y2 - y1;

  if (dx === 0 && dy === 0) {
    // El segmento es un punto
    return { x: x1, y: y1 };
  }

  // Calcular t (parámetro de proyección en el segmento)
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)));

  // Punto más cercano
  return {
    x: x1 + t * dx,
    y: y1 + t * dy,
  };
}

/**
 * Lógica de cálculo de snap - extraída para poder llamarla de forma síncrona
 */
function calculateSnap(
  map: maplibregl.Map,
  cursorPosition: [number, number],
  excludeNodeId: string | null,
  nodeSnapThreshold: number,
  edgeSnapThreshold: number
): SnapTarget | null {
  // Convertir cursor position a screen coordinates
  const cursorScreenPoint = map.project(cursorPosition);

  // Buscar nodos cercanos (excluir el nodo fuente) - threshold más generoso
  const nodeFeatures = map.queryRenderedFeatures(
    [
      [cursorScreenPoint.x - nodeSnapThreshold, cursorScreenPoint.y - nodeSnapThreshold],
      [cursorScreenPoint.x + nodeSnapThreshold, cursorScreenPoint.y + nodeSnapThreshold],
    ],
    {
      layers: ['canonical-network-nodes'],
    }
  );

  // Filtrar nodos excluidos y calcular distancias
  const nearbyNodes: SnapTarget[] = [];
  for (const feature of nodeFeatures) {
    if (feature.id === excludeNodeId) continue;

    // Filtrar nodos optimistas (preview temporales)
    if (feature.properties?._optimistic === true) {
      continue;
    }

    if (feature.geometry.type !== 'Point') continue;

    const nodeScreenPoint = map.project(feature.geometry.coordinates as [number, number]);
    const dx = nodeScreenPoint.x - cursorScreenPoint.x;
    const dy = nodeScreenPoint.y - cursorScreenPoint.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= nodeSnapThreshold) {
      nearbyNodes.push({
        type: 'node',
        id: feature.id as string,
        snapPoint: feature.geometry.coordinates as [number, number],
        distance,
        feature,
      });
    }
  }

  // Buscar edges cercanos - threshold REDUCIDO para menos agresividad
  const edgeFeatures = map.queryRenderedFeatures(
    [
      [cursorScreenPoint.x - edgeSnapThreshold, cursorScreenPoint.y - edgeSnapThreshold],
      [cursorScreenPoint.x + edgeSnapThreshold, cursorScreenPoint.y + edgeSnapThreshold],
    ],
    {
      layers: ['canonical-network-edges'],
    }
  );

  // Calcular punto más cercano en cada edge
  const nearbyEdges: SnapTarget[] = [];
  for (const feature of edgeFeatures) {
    // Filtrar edges optimistas (preview temporales)
    if (feature.properties?._optimistic === true) {
      continue;
    }

    if (feature.geometry.type !== 'LineString') continue;

    const coordinates = feature.geometry.coordinates as [number, number][];

    // Encontrar el punto más cercano en el edge
    let minDistance = Infinity;
    let closestPoint: [number, number] | null = null;

    for (let i = 0; i < coordinates.length - 1; i++) {
      const p1 = map.project(coordinates[i]);
      const p2 = map.project(coordinates[i + 1]);

      // Calcular punto más cercano en el segmento
      const closestOnSegment = getClosestPointOnSegment(
        cursorScreenPoint.x,
        cursorScreenPoint.y,
        p1.x,
        p1.y,
        p2.x,
        p2.y
      );

      const dx = closestOnSegment.x - cursorScreenPoint.x;
      const dy = closestOnSegment.y - cursorScreenPoint.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < minDistance) {
        minDistance = distance;
        // Convertir de screen a geo coordinates
        const geoPoint = map.unproject([closestOnSegment.x, closestOnSegment.y]);
        closestPoint = [geoPoint.lng, geoPoint.lat];
      }
    }

    if (closestPoint && minDistance <= edgeSnapThreshold) {
      nearbyEdges.push({
        type: 'edge',
        id: feature.id as string,
        snapPoint: closestPoint,
        distance: minDistance,
        feature,
      });
    }
  }

  // Combinar nodos y edges, ordenar por PRIORIDAD (Regla 14: nodo > edge), luego por distancia
  const allTargets = [...nearbyNodes, ...nearbyEdges].sort((a, b) => {
    // Regla 14: Prioridad nodo > edge > punto libre
    if (a.type === 'node' && b.type === 'edge') return -1;
    if (a.type === 'edge' && b.type === 'node') return 1;
    // Si mismo tipo, ordenar por distancia
    return a.distance - b.distance;
  });

  // Seleccionar el más cercano
  return allTargets.length > 0 ? allTargets[0] : null;
}

export interface UseSnapDetectionConfig {
  /** Si la detección está habilitada */
  enabled: boolean;
  /** Posición del cursor en coordenadas geo [lng, lat] */
  cursorPosition: [number, number] | null;
  /** ID del nodo a excluir (típicamente el nodo fuente de la rama) */
  excludeNodeId?: string | null;
  /** Distancia máxima de snap a NODOS en píxeles (por defecto: 15px) */
  nodeSnapThreshold?: number;
  /** Distancia máxima de snap a EDGES en píxeles (por defecto: 6px - menos agresivo) */
  edgeSnapThreshold?: number;
}

export type SnapTargetType = 'node' | 'edge';

export interface SnapTarget {
  /** Tipo de elemento al que hacer snap */
  type: SnapTargetType;
  /** ID del elemento */
  id: string;
  /** Coordenadas del punto de snap [lng, lat] */
  snapPoint: [number, number];
  /** Distancia en píxeles al punto de snap */
  distance: number;
  /** Feature completo (para acceso a propiedades) */
  feature: maplibregl.MapGeoJSONFeature;
}

export interface UseSnapDetectionResult {
  /** Target de snap más cercano (null si no hay ninguno) */
  snapTarget: SnapTarget | null;
  /** Función para calcular snap de forma síncrona (para usar en event handlers) */
  calculateSnapSync: (cursorPosition: [number, number]) => SnapTarget | null;
}

/**
 * Hook para detectar snap a elementos cercanos
 */
export function useSnapDetection({
  enabled,
  cursorPosition,
  excludeNodeId = null,
  nodeSnapThreshold = 15,
  edgeSnapThreshold = 6,
}: UseSnapDetectionConfig): UseSnapDetectionResult {
  const [snapTarget, setSnapTarget] = useState<SnapTarget | null>(null);

  // Ref para comparar snap anterior sin causar re-renders (FIX: Hallazgo #6)
  const prevSnapTargetRef = useRef<SnapTarget | null>(null);

  // Refs para mantener valores actualizados en callbacks
  const excludeNodeIdRef = useRef(excludeNodeId);
  const nodeSnapThresholdRef = useRef(nodeSnapThreshold);
  const edgeSnapThresholdRef = useRef(edgeSnapThreshold);

  // Mantener refs sincronizados
  useEffect(() => {
    excludeNodeIdRef.current = excludeNodeId;
  }, [excludeNodeId]);

  useEffect(() => {
    nodeSnapThresholdRef.current = nodeSnapThreshold;
  }, [nodeSnapThreshold]);

  useEffect(() => {
    edgeSnapThresholdRef.current = edgeSnapThreshold;
  }, [edgeSnapThreshold]);

  /**
   * Función síncrona para calcular snap - para usar en event handlers
   * Evita el problema de timing con el estado asíncrono
   */
  const calculateSnapSync = useCallback((cursorPos: [number, number]): SnapTarget | null => {
    const map = mapEngine.getMap();
    if (!map) return null;

    return calculateSnap(
      map,
      cursorPos,
      excludeNodeIdRef.current,
      nodeSnapThresholdRef.current,
      edgeSnapThresholdRef.current
    );
  }, []);

  // useEffect para mantener el estado (para componentes que dependen de él)
  useEffect(() => {
    if (!enabled || !cursorPosition) {
      setSnapTarget(null);
      prevSnapTargetRef.current = null;
      return;
    }

    const map = mapEngine.getMap();
    if (!map) {
      setSnapTarget(null);
      prevSnapTargetRef.current = null;
      return;
    }

    // Usar la función extraída para calcular snap
    const target = calculateSnap(
      map,
      cursorPosition,
      excludeNodeId,
      nodeSnapThreshold,
      edgeSnapThreshold
    );

    if (target) {
      console.log(`[SNAP] 🧲 Snap ACTIVADO:`, {
        type: target.type,
        id: target.id,
        distance: `${target.distance.toFixed(1)}px`,
        snapPoint: target.snapPoint,
        threshold: target.type === 'node' ? `${nodeSnapThreshold}px` : `${edgeSnapThreshold}px`,
      });
      setSnapTarget(target);
      prevSnapTargetRef.current = target;
    } else {
      const hadSnap = prevSnapTargetRef.current !== null;
      if (hadSnap) {
        console.log(`[SNAP] ❌ Snap DESACTIVADO (no hay targets cercanos)`);
      }
      setSnapTarget(null);
      prevSnapTargetRef.current = null;
    }
  }, [enabled, cursorPosition, excludeNodeId, nodeSnapThreshold, edgeSnapThreshold]);

  return { snapTarget, calculateSnapSync };
}
